import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { 
  Shield, 
  Lock, 
  CreditCard, 
  CheckCircle, 
  ArrowRight,
  Eye,
  EyeOff,
  Globe,
  Smartphone,
  Building,
  RefreshCw,
  AlertCircle,
  ChevronDown,
  HelpCircle,
  BadgeCheck,
  Banknote,
  Wallet,
  ShieldCheck,
  KeyRound,
  Server,
  FileCheck,
  Clock,
  Users,
  Zap,
  Award,
  CircleDollarSign,
  ArrowLeftRight,
  Receipt
} from 'lucide-react';

// Payment methods supported
const PAYMENT_METHODS = [
  {
    name: 'Carte di Credito',
    description: 'Visa, Mastercard, American Express',
    icon: CreditCard,
    logos: ['visa', 'mastercard', 'amex']
  },
  {
    name: 'Carte di Debito',
    description: 'Tutte le principali carte di debito',
    icon: CreditCard,
    logos: ['maestro', 'vpay']
  },
  {
    name: 'Digital Wallets',
    description: 'Apple Pay, Google Pay',
    icon: Smartphone,
    logos: ['applepay', 'googlepay']
  },
  {
    name: 'Bonifico SEPA',
    description: 'Per ordini di importo elevato',
    icon: Building,
    logos: ['sepa']
  }
];

// Security features
const SECURITY_FEATURES = [
  {
    icon: Lock,
    title: 'Crittografia SSL/TLS',
    description: 'Tutti i dati sono trasmessi con crittografia a 256 bit, lo stesso standard usato dalle banche.',
    highlight: true
  },
  {
    icon: EyeOff,
    title: 'Zero Dati Memorizzati',
    description: 'Rarifex NON memorizza mai i dati della tua carta. Non passano nemmeno dai nostri server.',
    highlight: true
  },
  {
    icon: ShieldCheck,
    title: 'PCI DSS Level 1',
    description: 'Stripe è certificato al massimo livello di sicurezza PCI, il più rigoroso del settore.',
    highlight: false
  },
  {
    icon: KeyRound,
    title: 'Autenticazione 3D Secure',
    description: 'Supporto completo per 3D Secure 2 (Verified by Visa, Mastercard SecureCode).',
    highlight: false
  },
  {
    icon: Server,
    title: 'Infrastruttura Sicura',
    description: 'I server Stripe sono monitorati 24/7 con protezione anti-frode avanzata.',
    highlight: false
  },
  {
    icon: FileCheck,
    title: 'Conformità GDPR',
    description: 'Stripe è pienamente conforme al GDPR europeo per la protezione dei dati personali.',
    highlight: false
  }
];

// Stripe benefits
const STRIPE_BENEFITS = [
  {
    icon: Globe,
    title: 'Leader Mondiale',
    description: 'Stripe elabora centinaia di miliardi di euro l\'anno per milioni di aziende in tutto il mondo, tra cui Amazon, Google, Shopify e Booking.com.'
  },
  {
    icon: Zap,
    title: 'Pagamenti Istantanei',
    description: 'I pagamenti vengono elaborati in tempo reale. Ricevi conferma immediata e l\'artigiano può iniziare a lavorare subito.'
  },
  {
    icon: Shield,
    title: 'Protezione Frodi',
    description: 'Sistemi di machine learning analizzano ogni transazione per bloccare automaticamente i pagamenti sospetti.'
  },
  {
    icon: RefreshCw,
    title: 'Rimborsi Semplici',
    description: 'In caso di problemi, i rimborsi vengono elaborati rapidamente e riaccreditati sulla stessa carta usata per il pagamento.'
  }
];

// How protected payments work
const PAYMENT_FLOW = [
  {
    step: '01',
    title: 'Checkout su Rarifex',
    description: 'Aggiungi i prodotti al carrello e procedi al checkout. Inserisci solo l\'indirizzo di spedizione.',
    icon: Wallet
  },
  {
    step: '02',
    title: 'Pagamento su Stripe',
    description: 'Vieni reindirizzato alla pagina sicura di Stripe. I tuoi dati di pagamento restano su Stripe, mai su Rarifex.',
    icon: CreditCard
  },
  {
    step: '03',
    title: 'Fondi Trattenuti',
    description: 'Stripe trattiene il pagamento in modo sicuro. L\'artigiano vede la conferma ma non riceve ancora i fondi.',
    icon: Lock
  },
  {
    step: '04',
    title: 'Conferma e Rilascio',
    description: 'Quando confermi la ricezione del prodotto, Stripe rilascia automaticamente il pagamento all\'artigiano.',
    icon: CheckCircle
  }
];

// FAQ
const FAQ_ITEMS = [
  {
    question: 'Rarifex memorizza i dati della mia carta?',
    answer: 'No, assolutamente. Rarifex non vede mai, non riceve mai e non memorizza mai i dati della tua carta di credito o debito. Quando effettui un pagamento, vieni reindirizzato direttamente alla pagina sicura di Stripe. I tuoi dati di pagamento viaggiano direttamente dal tuo browser a Stripe, senza mai passare dai server di Rarifex.'
  },
  {
    question: 'Cos\'è Stripe e perché dovrei fidarmi?',
    answer: 'Stripe è una delle più grandi piattaforme di pagamento al mondo, fondata nel 2010 e valutata oltre 50 miliardi di dollari. Gestisce i pagamenti per aziende come Amazon, Google, Shopify, Booking.com, Deliveroo e milioni di altre. È certificata PCI DSS Level 1, il massimo livello di sicurezza nel settore dei pagamenti.'
  },
  {
    question: 'Quando viene addebitato il pagamento?',
    answer: 'Il pagamento viene addebitato immediatamente quando completi il checkout. Tuttavia, i fondi vengono trattenuti da Stripe in modo sicuro e rilasciati all\'artigiano solo quando confermi di aver ricevuto il prodotto in buone condizioni.'
  },
  {
    question: 'Cosa succede se devo essere rimborsato?',
    answer: 'I rimborsi vengono elaborati attraverso Stripe e riaccreditati sulla stessa carta usata per il pagamento. I tempi di riaccredito dipendono dalla tua banca, generalmente 5-10 giorni lavorativi per carte di credito, 2-5 giorni per carte di debito.'
  },
  {
    question: 'Posso pagare con PayPal?',
    answer: 'Al momento accettiamo pagamenti con carte di credito/debito, Apple Pay, Google Pay e bonifico SEPA. PayPal non è attualmente supportato, ma stiamo valutando di aggiungerlo in futuro.'
  },
  {
    question: 'I pagamenti internazionali sono sicuri?',
    answer: 'Sì, Stripe gestisce pagamenti in oltre 135 valute e 47 paesi con lo stesso livello di sicurezza. La conversione valutaria, se necessaria, viene gestita automaticamente con tassi di cambio trasparenti.'
  },
  {
    question: 'Cosa significa "3D Secure"?',
    answer: '3D Secure è un ulteriore livello di sicurezza che richiede una verifica aggiuntiva (codice SMS, app della banca, impronta digitale) per autorizzare il pagamento. Protegge da utilizzi fraudolenti della carta anche se qualcuno ne conosce i dati.'
  },
  {
    question: 'Posso salvare la mia carta per acquisti futuri?',
    answer: 'Se crei un account su Rarifex, Stripe può memorizzare in modo sicuro un "token" della tua carta per velocizzare i pagamenti futuri. Anche in questo caso, i dati reali della carta restano esclusivamente su Stripe, mai su Rarifex.'
  }
];

// Trust badges/certifications
const CERTIFICATIONS = [
  { name: 'PCI DSS Level 1', description: 'Massimo livello di certificazione per la sicurezza dei pagamenti' },
  { name: 'SOC 1 & SOC 2', description: 'Audit indipendenti sui controlli di sicurezza' },
  { name: 'GDPR Compliant', description: 'Piena conformità al regolamento europeo sulla privacy' },
  { name: 'Strong Customer Authentication', description: 'Conforme alla direttiva PSD2 europea' }
];

export default function PaymentsPage() {
  const [expandedFaq, setExpandedFaq] = useState(null);

  return (
    <>
      <Helmet>
        <title>Pagamenti Sicuri su Rarifex | Checkout Protetto con Stripe | Zero Dati Memorizzati</title>
        <meta name="description" content="Scopri come funzionano i pagamenti sicuri su Rarifex. Checkout protetto con Stripe, crittografia SSL, zero dati delle carte memorizzati. Massima sicurezza per i tuoi acquisti di artigianato italiano." />
        <meta name="keywords" content="pagamenti sicuri rarifex, stripe pagamenti, checkout sicuro, pagamento protetto, carte di credito sicure, pci dss, 3d secure, acquisti sicuri online" />
        <link rel="canonical" href="https://www.rarifex.com/pagamenti" />
        <meta property="og:title" content="Pagamenti Sicuri su Rarifex | Checkout Protetto con Stripe" />
        <meta property="og:description" content="Pagamenti sicuri con Stripe. Rarifex non memorizza mai i dati della tua carta. Massima protezione per i tuoi acquisti." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.rarifex.com/pagamenti" />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-b from-slate-50 via-white to-blue-50/30">
        
        {/* HERO SECTION */}
        <section className="relative py-16 md:py-24 overflow-hidden">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-blue-500/10 via-transparent to-transparent" />
          
          <div className="container mx-auto px-4 relative z-10">
            <div className="max-w-4xl mx-auto text-center">
              <div className="inline-flex items-center gap-2 bg-green-100 text-green-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
                <ShieldCheck className="w-4 h-4" />
                Pagamenti 100% Sicuri
              </div>
              
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-slate-900 leading-tight mb-6">
                La tua sicurezza{' '}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600">
                  è la nostra priorità
                </span>
              </h1>
              
              <p className="text-xl md:text-2xl text-slate-600 mb-8 leading-relaxed max-w-3xl mx-auto">
                I pagamenti su Rarifex sono gestiti interamente da <strong className="text-slate-800">Stripe</strong>, 
                leader mondiale nei pagamenti online. 
                <span className="text-blue-600 font-semibold"> Noi non vediamo mai i dati della tua carta.</span>
              </p>

              {/* Key message box */}
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-2xl p-6 md:p-8 max-w-2xl mx-auto mb-10">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <EyeOff className="w-6 h-6 text-blue-600" />
                  </div>
                  <div className="text-left">
                    <h3 className="font-bold text-slate-900 mb-2">Zero dati memorizzati su Rarifex</h3>
                    <p className="text-slate-600">
                      I dati della tua carta di credito non passano mai dai nostri server. 
                      Viaggiano direttamente dal tuo browser a Stripe tramite connessione crittografata.
                    </p>
                  </div>
                </div>
              </div>

              {/* Trust indicators */}
              <div className="flex flex-wrap justify-center gap-6 md:gap-10">
                <div className="flex items-center gap-2 text-slate-700">
                  <Lock className="w-5 h-5 text-green-600" />
                  <span className="text-sm font-medium">Crittografia 256-bit</span>
                </div>
                <div className="flex items-center gap-2 text-slate-700">
                  <BadgeCheck className="w-5 h-5 text-green-600" />
                  <span className="text-sm font-medium">PCI DSS Level 1</span>
                </div>
                <div className="flex items-center gap-2 text-slate-700">
                  <Shield className="w-5 h-5 text-green-600" />
                  <span className="text-sm font-medium">Protezione Anti-frode</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* STRIPE INTRODUCTION */}
        <section className="py-12 md:py-16 bg-slate-900">
          <div className="container mx-auto px-4">
            <div className="max-w-5xl mx-auto">
              <div className="flex flex-col md:flex-row items-center gap-8 md:gap-12">
                {/* Stripe logo area */}
                <div className="flex-shrink-0">
                  <div className="w-32 h-32 md:w-40 md:h-40 bg-white rounded-2xl flex items-center justify-center shadow-xl p-6">
                    <img 
                      src="https://upload.wikimedia.org/wikipedia/commons/b/ba/Stripe_Logo%2C_revised_2016.svg"
                      alt="Stripe Logo"
                      className="w-full h-auto"
                    />
                  </div>
                </div>
                
                {/* Stripe description */}
                <div className="text-center md:text-left">
                  <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
                    Powered by Stripe
                  </h2>
                  <p className="text-slate-300 text-lg mb-4">
                    Stripe è la piattaforma di pagamento scelta da <strong className="text-white">milioni di aziende</strong> in tutto il mondo, 
                    tra cui Amazon, Google, Shopify, Booking.com, Deliveroo, e molte altre.
                  </p>
                  <p className="text-slate-400">
                    Elabora centinaia di miliardi di euro ogni anno con i più alti standard di sicurezza del settore.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* SECURITY FEATURES */}
        <section className="py-16 md:py-24">
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-16">
                <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
                  Sicurezza a ogni livello
                </h2>
                <p className="text-lg text-slate-600 max-w-2xl mx-auto">
                  Stripe utilizza le tecnologie più avanzate per proteggere ogni transazione.
                </p>
              </div>

              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {SECURITY_FEATURES.map((feature, index) => (
                  <Card 
                    key={index}
                    className={`p-6 border-slate-200 hover:shadow-lg transition-all ${
                      feature.highlight ? 'bg-gradient-to-br from-green-50 to-emerald-50 border-green-200' : 'bg-white'
                    }`}
                  >
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-4 ${
                      feature.highlight ? 'bg-green-100' : 'bg-slate-100'
                    }`}>
                      <feature.icon className={`w-6 h-6 ${feature.highlight ? 'text-green-600' : 'text-slate-600'}`} />
                    </div>
                    <h3 className="font-bold text-slate-900 mb-2">{feature.title}</h3>
                    <p className="text-sm text-slate-600">{feature.description}</p>
                  </Card>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* PAYMENT METHODS */}
        <section className="py-16 md:py-24 bg-slate-50">
          <div className="container mx-auto px-4">
            <div className="max-w-5xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
                  Metodi di pagamento accettati
                </h2>
                <p className="text-lg text-slate-600">
                  Paga come preferisci, sempre con la massima sicurezza.
                </p>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                {PAYMENT_METHODS.map((method, index) => (
                  <Card key={index} className="p-6 bg-white border-slate-200">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-blue-50 rounded-xl flex items-center justify-center flex-shrink-0">
                        <method.icon className="w-6 h-6 text-blue-600" />
                      </div>
                      <div>
                        <h3 className="font-bold text-slate-900 mb-1">{method.name}</h3>
                        <p className="text-sm text-slate-600">{method.description}</p>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* HOW PROTECTED PAYMENTS WORK */}
        <section className="py-16 md:py-24">
          <div className="container mx-auto px-4">
            <div className="max-w-5xl mx-auto">
              <div className="text-center mb-16">
                <div className="inline-flex items-center gap-2 bg-amber-100 text-amber-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
                  <Shield className="w-4 h-4" />
                  Pagamento Protetto
                </div>
                <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
                  Come funziona la protezione acquirente
                </h2>
                <p className="text-lg text-slate-600 max-w-2xl mx-auto">
                  Il tuo pagamento è trattenuto da Stripe fino a quando non confermi di aver ricevuto il prodotto.
                </p>
              </div>

              <div className="grid md:grid-cols-4 gap-6">
                {PAYMENT_FLOW.map((item, index) => (
                  <div key={index} className="relative">
                    {index < PAYMENT_FLOW.length - 1 && (
                      <div className="hidden md:block absolute top-10 left-full w-full h-0.5 bg-slate-200 -translate-x-1/2 z-0" />
                    )}
                    <Card className="p-6 bg-white border-slate-200 relative z-10 h-full">
                      <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-500 rounded-xl flex items-center justify-center mb-4">
                        <item.icon className="w-6 h-6 text-white" />
                      </div>
                      <div className="text-xs font-bold text-slate-400 mb-2">{item.step}</div>
                      <h3 className="font-semibold text-slate-900 mb-2">{item.title}</h3>
                      <p className="text-sm text-slate-600">{item.description}</p>
                    </Card>
                  </div>
                ))}
              </div>

              <div className="mt-12 bg-amber-50 border border-amber-200 rounded-2xl p-6 md:p-8">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <AlertCircle className="w-6 h-6 text-amber-600" />
                  </div>
                  <div>
                    <h3 className="font-bold text-slate-900 mb-2">L'artigiano riceve il pagamento solo dopo la tua conferma</h3>
                    <p className="text-slate-600">
                      Questo sistema protegge sia te che l'artigiano. Tu sai che i tuoi soldi sono al sicuro finché non ricevi il prodotto. 
                      L'artigiano sa che verrà pagato non appena tu confermi la ricezione. Tutti felici, zero rischi.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* STRIPE BENEFITS */}
        <section className="py-16 md:py-24 bg-gradient-to-b from-slate-900 to-slate-800">
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-16">
                <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                  Perché Stripe è la scelta migliore
                </h2>
                <p className="text-lg text-slate-300 max-w-2xl mx-auto">
                  Non è un caso che le più grandi aziende del mondo si affidino a Stripe.
                </p>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                {STRIPE_BENEFITS.map((benefit, index) => (
                  <div 
                    key={index}
                    className="bg-slate-800/50 rounded-2xl p-6 border border-slate-700/50 hover:border-blue-500/50 transition-all"
                  >
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-blue-500/20 rounded-xl flex items-center justify-center flex-shrink-0">
                        <benefit.icon className="w-6 h-6 text-blue-400" />
                      </div>
                      <div>
                        <h3 className="font-bold text-white mb-2">{benefit.title}</h3>
                        <p className="text-sm text-slate-400">{benefit.description}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Certifications */}
              <div className="mt-16">
                <h3 className="text-center text-lg font-semibold text-white mb-8">Certificazioni e Conformità</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {CERTIFICATIONS.map((cert, index) => (
                    <div key={index} className="bg-slate-800/30 rounded-xl p-4 text-center border border-slate-700/30">
                      <BadgeCheck className="w-8 h-8 text-green-400 mx-auto mb-2" />
                      <p className="text-white font-medium text-sm mb-1">{cert.name}</p>
                      <p className="text-slate-500 text-xs">{cert.description}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* DATA TRANSPARENCY */}
        <section className="py-16 md:py-24">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <div className="bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 rounded-3xl p-8 md:p-12 border border-blue-100">
                <div className="text-center mb-10">
                  <div className="w-16 h-16 bg-white rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg">
                    <EyeOff className="w-8 h-8 text-blue-600" />
                  </div>
                  <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
                    Cosa vede Rarifex, cosa vede Stripe
                  </h2>
                  <p className="text-lg text-slate-600">
                    Trasparenza totale su come vengono gestiti i tuoi dati di pagamento.
                  </p>
                </div>

                <div className="grid md:grid-cols-2 gap-8">
                  {/* What Rarifex sees */}
                  <div className="bg-white rounded-2xl p-6 shadow-sm">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center">
                        <Eye className="w-5 h-5 text-slate-600" />
                      </div>
                      <h3 className="font-bold text-slate-900">Rarifex vede</h3>
                    </div>
                    <ul className="space-y-3">
                      <li className="flex items-start gap-2">
                        <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                        <span className="text-slate-600">Conferma che il pagamento è andato a buon fine</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                        <span className="text-slate-600">Importo della transazione</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                        <span className="text-slate-600">Ultime 4 cifre della carta (es. **** 4242)</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                        <span className="text-slate-600">Tipo di carta (Visa, Mastercard, ecc.)</span>
                      </li>
                    </ul>
                  </div>

                  {/* What Rarifex does NOT see */}
                  <div className="bg-white rounded-2xl p-6 shadow-sm">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                        <EyeOff className="w-5 h-5 text-red-600" />
                      </div>
                      <h3 className="font-bold text-slate-900">Rarifex NON vede mai</h3>
                    </div>
                    <ul className="space-y-3">
                      <li className="flex items-start gap-2">
                        <Lock className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                        <span className="text-slate-600">Numero completo della carta</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <Lock className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                        <span className="text-slate-600">Data di scadenza</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <Lock className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                        <span className="text-slate-600">Codice CVV/CVC</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <Lock className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                        <span className="text-slate-600">PIN o password della carta</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-16 md:py-24 bg-slate-50">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto">
              <div className="text-center mb-12">
                <div className="inline-flex items-center gap-2 bg-slate-200 text-slate-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
                  <HelpCircle className="w-4 h-4" />
                  Domande Frequenti
                </div>
                <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
                  Hai domande sui pagamenti?
                </h2>
              </div>

              <div className="space-y-4">
                {FAQ_ITEMS.map((faq, index) => (
                  <div 
                    key={index}
                    className="bg-white rounded-xl border border-slate-200 overflow-hidden"
                  >
                    <button
                      onClick={() => setExpandedFaq(expandedFaq === index ? null : index)}
                      className="w-full px-6 py-4 flex items-center justify-between text-left hover:bg-slate-50 transition-colors"
                    >
                      <span className="font-medium text-slate-900 pr-4">{faq.question}</span>
                      <ChevronDown 
                        className={`w-5 h-5 text-slate-400 flex-shrink-0 transition-transform ${
                          expandedFaq === index ? 'rotate-180' : ''
                        }`}
                      />
                    </button>
                    {expandedFaq === index && (
                      <div className="px-6 pb-4">
                        <p className="text-slate-600">{faq.answer}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* CTA SECTION */}
        <section className="py-16 md:py-24 bg-gradient-to-r from-blue-600 to-indigo-600">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              <ShieldCheck className="w-12 h-12 text-white/80 mx-auto mb-6" />
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
                Acquista con fiducia
              </h2>
              <p className="text-xl text-white/80 mb-8 max-w-2xl mx-auto">
                Ogni transazione su Rarifex è protetta dalla tecnologia Stripe. 
                I tuoi dati sono al sicuro, sempre.
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                <Link to="/products">
                  <Button size="lg" className="bg-white text-blue-600 hover:bg-slate-100">
                    <CreditCard className="w-5 h-5 mr-2" />
                    Inizia ad Acquistare
                  </Button>
                </Link>
                <Link to="/come-acquistare">
                  <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10">
                    <ArrowRight className="w-5 h-5 mr-2" />
                    Guida all'Acquisto
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </section>

      </div>
    </>
  );
}
